// ============================================================================
// Native PDF export using jsPDF primitives, driven by the i18n `t` function
// passed in from the calling component. Every user-visible string on the two
// A4 pages is looked up via t() so the PDF language matches the UI.
//
// Polish diacritics fall outside jsPDF's default WinAnsi Helvetica; the
// caller therefore also passes `sanitizeForPdf` which transliterates those
// characters to their closest Latin-1 equivalent before we draw them.
// ============================================================================

import { CURRENCIES, UPGRADE_DEFINITIONS } from "./calculations";
import { formatCurrency as fmtCurrency, formatKwh as fmtKwh, formatNumber as fmtNumber } from "./i18n";
import { upperKeepUnits } from "./caseSafe";

// Palette
const NAVY = [0, 45, 91];
const GREEN = [5, 150, 105];
const ORANGE = [234, 88, 12];
const INK = [15, 23, 42];
const MUTED = [100, 116, 139];
const LIGHT_BORDER = [226, 232, 240];
const SURFACE = [248, 250, 252];
const DIM = [148, 163, 184];
const RECOMMENDED_BG = [240, 247, 255];

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 15;
const COL_W = PAGE_W - MARGIN * 2;

function setFillRGB(pdf, rgb) { pdf.setFillColor(rgb[0], rgb[1], rgb[2]); }
function setDrawRGB(pdf, rgb) { pdf.setDrawColor(rgb[0], rgb[1], rgb[2]); }
function setTextRGB(pdf, rgb) { pdf.setTextColor(rgb[0], rgb[1], rgb[2]); }

// Draw a single-line string that must not exceed `maxWidth` (mm). If it
// does, iteratively trim from the right and append "…" until it fits.
// The current pdf font & size are used, so callers must set those first.
function drawFitted(pdf, text, x, y, maxWidth, options = {}) {
  const s = String(text ?? "");
  if (pdf.getTextWidth(s) <= maxWidth) {
    pdf.text(s, x, y, options);
    return;
  }
  const ellipsis = "...";
  let lo = 0;
  let hi = s.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (pdf.getTextWidth(s.slice(0, mid) + ellipsis) <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  const trimmed = s.slice(0, lo).replace(/\s+$/, "");
  pdf.text(trimmed + ellipsis, x, y, options);
}

// Draw wrapped multi-line text bounded to `maxLines` lines. Extra content
// is truncated with an ellipsis on the final visible line.
function drawWrapped(pdf, text, x, y, maxWidth, lineH, maxLines, options = {}) {
  const raw = String(text ?? "");
  const lines = pdf.splitTextToSize(raw, maxWidth);
  let visible = lines;
  if (lines.length > maxLines) {
    visible = lines.slice(0, maxLines);
    const last = visible[maxLines - 1] || "";
    let truncated = last.replace(/\s+\S*$/, "");
    while (
      truncated.length > 0 &&
      pdf.getTextWidth(`${truncated} ...`) > maxWidth
    ) {
      truncated = truncated.replace(/\s+\S*$|.$/, "");
    }
    visible[maxLines - 1] = `${truncated} ...`;
  }
  visible.forEach((line, i) => {
    pdf.text(line, x, y + i * lineH, options);
  });
  return visible.length * lineH;
}

function drawPageHeader(pdf, S, { clientName, projectName, projectDate, pageLabel, contactEmail, company, jobTitle }) {
  // Right-side "PAGE X OF 2" — set first so we can reserve space next to it.
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  setTextRGB(pdf, MUTED);
  const pageLabelText = S(upperKeepUnits(pageLabel));
  const pageLabelWidth = pdf.getTextWidth(pageLabelText);
  pdf.text(pageLabelText, PAGE_W - MARGIN, MARGIN, { align: "right" });

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  setTextRGB(pdf, MUTED);
  const headerRuleMax = PAGE_W - MARGIN * 2 - pageLabelWidth - 6;
  drawFitted(pdf, S(S.t("pdf.header_line")), MARGIN, MARGIN, headerRuleMax);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(18);
  setTextRGB(pdf, INK);
  drawFitted(pdf, S(clientName || "-"), MARGIN, MARGIN + 9, PAGE_W - MARGIN * 2);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  setTextRGB(pdf, MUTED);
  const subtitleLine1 = `${projectName || "-"}   ·   ${S.t("pdf.prepared")} ${projectDate || "-"}`;
  drawFitted(pdf, S(subtitleLine1), MARGIN, MARGIN + 14, PAGE_W - MARGIN * 2);

  const contactBits = [];
  if (contactEmail && contactEmail.trim().length > 0) contactBits.push(contactEmail.trim());
  if (jobTitle && jobTitle.trim().length > 0) contactBits.push(jobTitle.trim());
  if (company && company.trim().length > 0) contactBits.push(company.trim());

  let ruleY = MARGIN + 18;
  if (contactBits.length > 0) {
    pdf.setFontSize(8);
    drawFitted(
      pdf,
      S(`${S.t("pdf.contact")}: ${contactBits.join("  ·  ")}`),
      MARGIN,
      MARGIN + 19,
      PAGE_W - MARGIN * 2
    );
    ruleY = MARGIN + 22;
  }

  setDrawRGB(pdf, LIGHT_BORDER);
  pdf.setLineWidth(0.3);
  pdf.line(MARGIN, ruleY, PAGE_W - MARGIN, ruleY);
  return ruleY + 5;
}

function drawPageFooter(pdf, S, { pageNum, pageTotal }) {
  const y = PAGE_H - 10;
  setDrawRGB(pdf, LIGHT_BORDER);
  pdf.setLineWidth(0.3);
  pdf.line(MARGIN, y - 4, PAGE_W - MARGIN, y - 4);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  setTextRGB(pdf, MUTED);
  pdf.text(S(S.t("pdf.footer")), MARGIN, y);
  pdf.setFont("helvetica", "bold");
  pdf.text(
    S(S.t("pdf.page_of", { n: pageNum, total: pageTotal })),
    PAGE_W - MARGIN,
    y,
    { align: "right" }
  );
}

function drawSectionHeading(pdf, S, y, title, eyebrow) {
  // Draw eyebrow first so we can reserve its width when trimming the title.
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  setTextRGB(pdf, MUTED);
  const eyebrowText = S(upperKeepUnits(eyebrow));
  const eyebrowW = pdf.getTextWidth(eyebrowText);
  pdf.text(eyebrowText, PAGE_W - MARGIN, y, { align: "right" });

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  setTextRGB(pdf, INK);
  const titleMax = PAGE_W - MARGIN * 2 - eyebrowW - 6;
  drawFitted(pdf, S(title), MARGIN, y, titleMax);
}

function drawComparisonTable(pdf, S, x, y, w, rows, symbol, vsHeader) {
  setFillRGB(pdf, [255, 255, 255]);
  setDrawRGB(pdf, LIGHT_BORDER);
  pdf.setLineWidth(0.3);

  const col = { option: 70, kwh: 26, cost: 26, co2: 26, vs: 18 };
  const colX = {
    option: x + 4,
    kwh: x + col.option + col.kwh,
    cost: x + col.option + col.kwh + col.cost,
    co2: x + col.option + col.kwh + col.cost + col.co2,
    vs: x + w - 4,
  };
  const HEADER_FONT = 6.5;
  const HEADER_LINE_H = 2.7;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(HEADER_FONT);
  const headers = [
    [S.t("results.option"), colX.option, "left", col.option - 8],
    [S.t("results.annual_kwh"), colX.kwh, "right", col.kwh - 3],
    [S.t("results.annual_cost"), colX.cost, "right", col.cost - 3],
    [S.t("results.co2_10yr"), colX.co2, "right", col.co2 - 3],
    [vsHeader, colX.vs, "right", col.vs - 2],
  ].map(([txt, hx, align, maxW]) => {
    const text = S(upperKeepUnits(txt));
    let size = HEADER_FONT;
    pdf.setFontSize(size);
    // Shrink so no single word is split mid-word across lines.
    while (size > 5 && text.split(" ").some((wd) => pdf.getTextWidth(wd) > maxW)) {
      size -= 0.25;
      pdf.setFontSize(size);
    }
    const lines = pdf.splitTextToSize(text, maxW).slice(0, 2);
    pdf.setFontSize(HEADER_FONT);
    return { lines, hx, align, size };
  });
  const headerH = headers.some((h) => h.lines.length > 1) ? 10.5 : 8;

  const nameMaxW = col.option - 12;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  const layouts = rows.map((r) => {
    const lines = pdf.splitTextToSize(S(S.t(r.nameKey)), nameMaxW).slice(0, 2);
    const nameBlockH = lines.length * 3.6 + (r.recommended ? 4.2 : 0);
    const rowH = Math.max(11, nameBlockH + 5);
    return { lines, rowH };
  });
  const totalH = headerH + layouts.reduce((s, l) => s + l.rowH, 0);

  pdf.roundedRect(x, y, w, totalH, 2, 2, "D");

  setFillRGB(pdf, SURFACE);
  pdf.rect(x + 0.3, y + 0.3, w - 0.6, headerH - 0.1, "F");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(HEADER_FONT);
  setTextRGB(pdf, MUTED);
  headers.forEach(({ lines, hx, align, size }) => {
    pdf.setFontSize(size);
    lines.forEach((line, i) => {
      const ly = y + headerH - 2.8 - (lines.length - 1 - i) * HEADER_LINE_H;
      pdf.text(line, hx, ly, { align });
    });
  });

  setDrawRGB(pdf, LIGHT_BORDER);
  pdf.setLineWidth(0.2);
  pdf.line(x, y + headerH, x + w, y + headerH);

  let cursorY = y + headerH;

  rows.forEach((r, idx) => {
    const layout = layouts[idx];
    const rowH = layout.rowH;
    const rowY = cursorY;
    cursorY += rowH;

    if (r.recommended) {
      setFillRGB(pdf, RECOMMENDED_BG);
      pdf.rect(x + 0.3, rowY + 0.3, w - 0.6, rowH - 0.6, "F");
    }
    if (idx > 0) {
      setDrawRGB(pdf, LIGHT_BORDER);
      pdf.setLineWidth(0.15);
      pdf.line(x + 4, rowY, x + w - 4, rowY);
    }

    const badgeX = colX.option + 1;
    const badgeY = rowY + rowH / 2;
    setFillRGB(pdf, r.recommended ? NAVY : [241, 245, 249]);
    pdf.circle(badgeX, badgeY, 2.5, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    setTextRGB(pdf, r.recommended ? [255, 255, 255] : MUTED);
    pdf.text(r.letter, badgeX, badgeY + 1, { align: "center" });

    const textX = colX.option + 6;
    const nameLines = layout.lines;
    const nameBlockH = nameLines.length * 3.6 + (r.recommended ? 4.2 : 0);
    let textY = rowY + (rowH - nameBlockH) / 2 + 2.6;

    if (r.recommended) {
      const pillW = 20;
      const pillH = 3.4;
      setFillRGB(pdf, NAVY);
      pdf.roundedRect(textX, textY - 2.6, pillW, pillH, 0.8, 0.8, "F");
      pdf.setFontSize(5.5);
      setTextRGB(pdf, [255, 255, 255]);
      pdf.setFont("helvetica", "bold");
      pdf.text(S(upperKeepUnits(S.t("common.recommended"))), textX + pillW / 2, textY - 0.4, { align: "center" });
      textY += 4.2;
    }

    pdf.setFont("helvetica", r.recommended ? "bold" : "normal");
    pdf.setFontSize(9.5);
    setTextRGB(pdf, INK);
    nameLines.forEach((line, i) => {
      pdf.text(line, textX, textY + i * 3.6);
    });

    const numY = rowY + rowH / 2 + 1.5;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    setTextRGB(pdf, INK);
    pdf.text(S(fmtKwh(r.kwh, S.numberLocale)), colX.kwh, numY, { align: "right" });
    pdf.text(S(fmtCurrency(r.cost, symbol, S.numberLocale)), colX.cost, numY, { align: "right" });
    pdf.text(
      S(`${fmtNumber(r.co2, S.numberLocale, { maximumFractionDigits: 1, minimumFractionDigits: 1 })} ${S.t("units.tonne_short")}`),
      colX.co2,
      numY,
      { align: "right" }
    );

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    setTextRGB(pdf, r.reduction === null ? DIM : GREEN);
    pdf.text(
      r.reduction === null
        ? "-"
        : `-${fmtNumber(r.reduction, S.numberLocale, { maximumFractionDigits: 0 })}%`,
      colX.vs,
      numY,
      { align: "right" }
    );
  });

  return totalH;
}

function drawHeroMetric(pdf, S, x, y, w, h, { eyebrow, value, unit, caption, valueColor }) {
  setFillRGB(pdf, [255, 255, 255]);
  setDrawRGB(pdf, LIGHT_BORDER);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(x, y, w, h, 3, 3, "FD");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  setTextRGB(pdf, MUTED);
  drawFitted(pdf, S(upperKeepUnits(eyebrow)), x + 7, y + 7, w - 14);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(26);
  setTextRGB(pdf, valueColor || NAVY);
  const valueStr = S(value);
  const unitStr = unit ? S(unit) : "";
  // Reserve horizontal room for the unit next to the value so it never
  // overflows the tile.
  const unitW = (() => {
    if (!unitStr) return 0;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);
    const w2 = pdf.getTextWidth(unitStr);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(26);
    return w2 + 3;
  })();
  const valueMax = w - 14 - unitW;
  drawFitted(pdf, valueStr, x + 7, y + 20, valueMax);
  const valueWidth = Math.min(pdf.getTextWidth(valueStr), valueMax);

  if (unitStr) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);
    setTextRGB(pdf, MUTED);
    pdf.text(unitStr, x + 7 + valueWidth + 3, y + 20);
  }

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.5);
  setTextRGB(pdf, MUTED);
  // Caption starts at y + 28. Tile bottom is y + h. Leave 3mm padding.
  const captionMaxH = h - 28 - 3;
  const captionLineH = 3.6;
  const captionMaxLines = Math.max(1, Math.floor(captionMaxH / captionLineH));
  drawWrapped(pdf, S(caption), x + 7, y + 28, w - 14, captionLineH, captionMaxLines);
}

function drawBarChart(pdf, S, x, y, w, h, { scenarios }) {
  const rows = scenarios.rows;
  const isNewBuild = scenarios.isNewBuild;
  const barColors = isNewBuild
    ? [[100, 116, 139], NAVY]
    : [[148, 163, 184], [100, 116, 139], NAVY];
  const bars = rows.map((r, i) => ({
    v: r.kwh,
    letter: r.letter,
    fullName: S(S.t(r.nameKey)),
    color: barColors[i],
  }));

  const legendRowH = 4.2;
  const legendH = 4 + bars.length * legendRowH;
  const chartAreaH = h - legendH;

  setDrawRGB(pdf, LIGHT_BORDER);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(x, y, w, h, 2, 2, "D");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  setTextRGB(pdf, INK);
  pdf.text(
    S(isNewBuild ? S.t("pdf.chart_title_new") : S.t("pdf.chart_title_reno")),
    x + 6,
    y + 7
  );

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  setTextRGB(pdf, MUTED);
  pdf.text(S(S.t("pdf.chart_subtitle")), x + 6, y + 12);

  const padL = 24, padR = 10, padT = 18, padB = 10;
  const plotX = x + padL;
  const plotY = y + padT;
  const plotW = w - padL - padR;
  const plotH = chartAreaH - padT - padB;

  const maxVal = Math.max(...bars.map((b) => b.v), 1);
  const niceCeil = (v) => {
    if (v === 0) return 1;
    const mag = Math.pow(10, Math.floor(Math.log10(v)));
    const n = v / mag;
    let mult = 1;
    if (n <= 1) mult = 1;
    else if (n <= 2) mult = 2;
    else if (n <= 5) mult = 5;
    else mult = 10;
    return mult * mag;
  };
  const yMax = niceCeil(maxVal * 1.15);

  const yTicks = 4;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  setTextRGB(pdf, MUTED);
  for (let i = 0; i <= yTicks; i++) {
    const val = (yMax * i) / yTicks;
    const yy = plotY + plotH - (plotH * i) / yTicks;
    setDrawRGB(pdf, [241, 245, 249]);
    pdf.setLineWidth(0.2);
    pdf.line(plotX, yy, plotX + plotW, yy);
    pdf.text(`${Math.round(val / 1000)}k`, plotX - 2, yy + 1.2, { align: "right" });
  }

  const nBars = bars.length;
  const slotW = plotW / nBars;
  const barW = slotW * 0.55;

  bars.forEach((b, i) => {
    const bx = plotX + slotW * i + (slotW - barW) / 2;
    const bh = (b.v / yMax) * plotH;
    setFillRGB(pdf, b.color);
    pdf.rect(bx, plotY + plotH - bh, barW, bh, "F");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    setTextRGB(pdf, INK);
    pdf.text(S(fmtKwh(b.v, S.numberLocale)), bx + barW / 2, plotY + plotH - bh - 2, { align: "center" });

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    setTextRGB(pdf, INK);
    pdf.text(b.letter, bx + barW / 2, plotY + plotH + 6, { align: "center" });
  });

  const legendY = y + chartAreaH + 2;
  bars.forEach((b, i) => {
    const rowY = legendY + i * legendRowH;
    setFillRGB(pdf, b.color);
    pdf.rect(x + 6, rowY - 2, 2.8, 2.8, "F");
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    setTextRGB(pdf, [71, 85, 105]);
    const label = `${b.letter} · ${b.fullName}`;
    const [line] = pdf.splitTextToSize(label, w - 18);
    pdf.text(line, x + 11, rowY + 0.1);
  });
}

function drawBudgetTable(pdf, S, x, y, w, rows) {
  setFillRGB(pdf, [255, 255, 255]);
  setDrawRGB(pdf, LIGHT_BORDER);
  pdf.setLineWidth(0.3);
  const rowH = 9;
  const totalH = rowH * rows.length + 4;
  pdf.roundedRect(x, y, w, totalH, 2, 2, "D");

  rows.forEach((row, idx) => {
    const rowY = y + 2 + rowH * idx;
    const isLast = idx === rows.length - 1;
    if (!isLast) {
      setDrawRGB(pdf, LIGHT_BORDER);
      pdf.setLineWidth(0.2);
      pdf.line(x + 6, rowY + rowH, x + w - 6, rowY + rowH);
    }

    // Draw value first (right-aligned) so we can measure and reserve
    // horizontal space for the label on the left.
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(isLast ? 12 : 10);
    setTextRGB(pdf, isLast ? NAVY : INK);
    const valueStr = S(row.value);
    const valueWidth = pdf.getTextWidth(valueStr);
    pdf.text(valueStr, x + w - 6, rowY + rowH / 2 + 1.3, { align: "right" });

    pdf.setFont("helvetica", isLast ? "bold" : "normal");
    pdf.setFontSize(isLast ? 10 : 9.5);
    setTextRGB(pdf, isLast ? INK : [71, 85, 105]);
    const labelMax = w - 12 - valueWidth - 6;
    drawFitted(pdf, S(row.label), x + 6, rowY + rowH / 2 + 1.3, labelMax);
  });
  return totalH;
}

function drawBusinessCaseTile(pdf, S, x, y, w, h, { label, value, caption, valueColor }) {
  setFillRGB(pdf, SURFACE);
  setDrawRGB(pdf, LIGHT_BORDER);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(x, y, w, h, 3, 3, "FD");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  setTextRGB(pdf, MUTED);
  drawFitted(pdf, S(upperKeepUnits(label)), x + 6, y + 7, w - 12);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(22);
  setTextRGB(pdf, valueColor || NAVY);
  drawFitted(pdf, S(value), x + 6, y + 20, w - 12);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.5);
  setTextRGB(pdf, MUTED);
  const captionMaxH = h - 26 - 3;
  const captionLineH = 3.6;
  const captionMaxLines = Math.max(1, Math.floor(captionMaxH / captionLineH));
  drawWrapped(pdf, S(caption), x + 6, y + 26, w - 12, captionLineH, captionMaxLines);
}

function drawAssumptionCell(pdf, S, x, y, w, label, value) {
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  setTextRGB(pdf, MUTED);
  drawFitted(pdf, S(upperKeepUnits(label)), x, y, w);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  setTextRGB(pdf, INK);
  drawFitted(pdf, S(value), x, y + 5.5, w);
}

export async function generateBusinessCasePdf({
  state,
  results,
  currencyCode,
  executiveSummary,
  t,
  locale,
  sanitizeForPdf,
}) {
  const jsPDFModule = await import("jspdf");
  const JsPDF = jsPDFModule.jsPDF || jsPDFModule.default;
  const pdf = new JsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const symbol = CURRENCIES[currencyCode].symbol;
  const localeMeta = (typeof locale === "string" ? locale : "en");
  // Map ui locale -> intl locale (fallback en-GB)
  const numberLocaleMap = {
    en: "en-GB", fi: "fi-FI", sv: "sv-SE", pl: "pl-PL", it: "it-IT",
    fr: "fr-FR", da: "da-DK", no: "nb-NO", nl: "nl-NL",
  };
  const numberLocale = numberLocaleMap[localeMeta] || "en-GB";

  // Sanitiser wrapper — swaps chars outside jsPDF's WinAnsi range (Polish).
  const S = (str) => (sanitizeForPdf ? sanitizeForPdf(str) : str);
  S.t = t;
  S.numberLocale = numberLocale;

  const paybackLabel =
    results.payback === null
      ? t("common.na")
      : results.payback <= 0
      ? t("common.immediate")
      : `${fmtNumber(results.payback, numberLocale, { maximumFractionDigits: 1, minimumFractionDigits: 1 })} ${t("common.years")}`;

  // getScenarios lives in calculations.js and is imported at the top file.
  const { getScenarios } = await import("./calculations");
  const scenarios = getScenarios(results, state);
  const isNewBuild = scenarios.isNewBuild;

  // ------------------------------------------------------------------ PAGE 1
  let y = drawPageHeader(pdf, S, {
    clientName: state.clientName,
    projectName: state.projectName,
    projectDate: state.projectDate,
    contactEmail: state.contactEmail,
    company: state.company,
    jobTitle: state.jobTitle,
    pageLabel: t("pdf.page_1_label"),
  });

  drawSectionHeading(
    pdf,
    S,
    y,
    isNewBuild ? t("results.comparison_2") : t("results.comparison_3"),
    t("pdf.eyebrow_scenarios")
  );
  y += 5;

  const compH = drawComparisonTable(
    pdf,
    S,
    MARGIN,
    y,
    COL_W,
    scenarios.rows,
    symbol,
    t(scenarios.vsColumnHeaderKey)
  );
  y += compH + 4;
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(7.5);
  setTextRGB(pdf, DIM);
  const co2Factor = fmtNumber(results.co2PerKwh, numberLocale, { maximumFractionDigits: 3 });
  pdf.text(S(t("pdf.co2_note", { factor: co2Factor })), MARGIN, y);
  y += 8;

  drawSectionHeading(pdf, S, y, t("pdf.env_title"), t("pdf.eyebrow_uplift"));
  y += 5;

  const heroH = 42;
  const heroW = (COL_W - 6) / 2;
  drawHeroMetric(pdf, S, MARGIN, y, heroW, heroH, {
    eyebrow: isNewBuild ? t("pdf.additional_annual_saving") : t("pdf.extra_from_helvar"),
    value: fmtKwh(results.savedB_to_C, numberLocale),
    unit: t("units.kwh_yr"),
    caption: isNewBuild ? t("pdf.env_caption_new") : t("pdf.env_caption_reno"),
    valueColor: NAVY,
  });
  drawHeroMetric(pdf, S, MARGIN + heroW + 6, y, heroW, heroH, {
    eyebrow: isNewBuild ? t("pdf.additional_10yr") : t("pdf.extra_10yr_helvar"),
    value: fmtNumber(results.co2B_to_C, numberLocale, { maximumFractionDigits: 1, minimumFractionDigits: 1 }),
    unit: t("units.tonnes"),
    caption: t("pdf.co2_caption", { factor: co2Factor }),
    valueColor: GREEN,
  });
  y += heroH + 6;

  drawBarChart(pdf, S, MARGIN, y, COL_W, 68, { scenarios });
  drawPageFooter(pdf, S, { pageNum: 1, pageTotal: 2 });

  // ------------------------------------------------------------------ PAGE 2
  pdf.addPage();
  y = drawPageHeader(pdf, S, {
    clientName: state.clientName,
    projectName: state.projectName,
    projectDate: state.projectDate,
    contactEmail: state.contactEmail,
    company: state.company,
    jobTitle: state.jobTitle,
    pageLabel: t("pdf.page_2_label"),
  });

  drawSectionHeading(pdf, S, y, t("results.total_capex"), t("pdf.eyebrow_capex"));
  y += 5;
  const budgetH = drawBudgetTable(pdf, S, MARGIN, y, COL_W, [
    { label: t("capex.luminaire_cost"), value: fmtCurrency(results.luminaireCost, symbol, numberLocale) },
    { label: t("capex.installation"), value: fmtCurrency(results.installationCost, symbol, numberLocale) },
    { label: t("capex.helvar_system"), value: fmtCurrency(results.helvarSystemCost, symbol, numberLocale) },
    { label: t("results.total_capex"), value: fmtCurrency(results.totalProjectCapex, symbol, numberLocale) },
  ]);
  y += budgetH + 6;

  drawSectionHeading(pdf, S, y, t("results.business_case"), t("pdf.eyebrow_payback"));
  y += 5;
  const tileH = 36;
  const tileW = (COL_W - 6) / 2;
  drawBusinessCaseTile(pdf, S, MARGIN, y, tileW, tileH, {
    label: results.compareCompetitor ? t("results.controls_premium") : t("results.helvar_investment"),
    value: fmtCurrency(results.controlsPremiumCapex, symbol, numberLocale),
    caption: results.compareCompetitor
      ? t("pdf.controls_premium_caption")
      : t("pdf.helvar_investment_caption"),
    valueColor: ORANGE,
  });
  drawBusinessCaseTile(pdf, S, MARGIN + tileW + 6, y, tileW, tileH, {
    label: t("results.payback_period"),
    value: paybackLabel,
    caption:
      t("pdf.payback_driver", {
        amount: fmtCurrency(results.financialB_to_C, symbol, numberLocale),
      }) + (results.compareCompetitor ? "" : t("pdf.payback_worst_case_addendum")),
    valueColor: NAVY,
  });
  y += tileH + 6;

  drawSectionHeading(pdf, S, y, t("pdf.assumed_metrics"), t("pdf.eyebrow_inputs"));
  y += 5;

  const gridCols = 4;
  const gridColW = COL_W / gridCols;
  const isReno = state.projectType !== "newBuild";
  const cells = [
    isReno
      ? [t("pdf.assumed.existing_luminaires"), fmtNumber(state.existingLuminaires ?? state.luminaires, numberLocale)]
      : [t("pdf.assumed.luminaires"), fmtNumber(state.luminaires, numberLocale)],
    ...(isReno ? [[t("pdf.assumed.new_luminaires"), fmtNumber(state.luminaires, numberLocale)]] : []),
    [t("pdf.assumed.burning"), `${state.burningHoursPerWeek}h`],
    [t("pdf.assumed.existing_wattage"), `${state.existingWattage}W`],
    [t("pdf.assumed.proposed_wattage"), `${state.proposedWattage}W`],
    [t("pdf.assumed.existing_runtime"), `${state.existingRuntimePct}%`],
    [t("pdf.assumed.upgrade_runtime"), `${state.upgradeRuntimePct}%`],
    [t("pdf.assumed.energy_price"), `${symbol}${symbol.length > 1 ? " " : ""}${fmtNumber(state.pricePerKwh, numberLocale, { maximumFractionDigits: 3 })}/kWh`],
    [t("pdf.assumed.currency"), currencyCode],
    [t("pdf.assumed.co2_factor"), `${co2Factor} kg/kWh`],
    [t("pdf.assumed.optA"), fmtKwh(results.kwhA, numberLocale)],
    [t("pdf.assumed.optB"), fmtKwh(results.kwhB, numberLocale)],
    [t("pdf.assumed.optC"), fmtKwh(results.kwhC, numberLocale)],
    [t("pdf.assumed.b_to_c_savings"), `${fmtKwh(results.savedB_to_C, numberLocale)} kWh`],
  ];
  const rowH = 11;
  const gridRows = Math.ceil(cells.length / gridCols);
  const metricsH = 6 + rowH * gridRows + 4;
  setDrawRGB(pdf, LIGHT_BORDER);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(MARGIN, y, COL_W, metricsH, 2, 2, "D");

  cells.forEach(([l, v], i) => {
    const col = i % gridCols;
    const row = Math.floor(i / gridCols);
    const cx = MARGIN + gridColW * col + 5;
    const cy = y + 6 + rowH * row;
    drawAssumptionCell(pdf, S, cx, cy, gridColW - 10, l, v);
  });
  y += metricsH + 6;

  drawSectionHeading(pdf, S, y, t("pdf.strategies_title"), t("pdf.eyebrow_helvar"));
  y += 5;
  const stratRowH = 7;
  const tableH = stratRowH * UPGRADE_DEFINITIONS.length;
  setDrawRGB(pdf, LIGHT_BORDER);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(MARGIN, y, COL_W, tableH, 2, 2, "D");

  UPGRADE_DEFINITIONS.forEach((def, idx) => {
    const u = state.upgrades?.[def.id];
    const enabled = !!u?.enabled;
    const pct = u?.savingsPct ?? def.defaultPct;
    const coverage = def.hasCoverage
      ? (u?.coveragePct ?? def.defaultCoveragePct ?? 100)
      : null;
    const rowY = y + stratRowH * idx;

    if (idx % 2 === 0) {
      setFillRGB(pdf, SURFACE);
      pdf.rect(MARGIN + 0.3, rowY + 0.3, COL_W - 0.6, stratRowH - 0.6, "F");
    }
    setFillRGB(pdf, enabled ? NAVY : [203, 213, 225]);
    pdf.circle(MARGIN + 6, rowY + stratRowH / 2, 1.1, "F");

    // Right-aligned pct badge — measured first so the left-side label
    // never encroaches on it.
    const pctText = enabled ? `-${pct}%` : S(t("pdf.strategy_off"));
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    setTextRGB(pdf, enabled ? NAVY : [203, 213, 225]);
    const pctWidth = pdf.getTextWidth(pctText);
    pdf.text(
      pctText,
      PAGE_W - MARGIN - 6,
      rowY + stratRowH / 2 + 1,
      { align: "right" }
    );

    const rowTextRight = PAGE_W - MARGIN - 6 - pctWidth - 4;
    const rowTextLeft = MARGIN + 11;
    const rowTextMax = rowTextRight - rowTextLeft;

    const strategyLabel = def.insights
      ? `${t(`strategy.${def.id}.label`)} (Helvar Insights)`
      : t(`strategy.${def.id}.label`);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9.5);
    setTextRGB(pdf, enabled ? INK : DIM);

    const showCoverageNote =
      enabled && coverage !== null && coverage < 100;
    if (showCoverageNote) {
      const coverageNote = S(`· ${t("pdf.strategy_applied", { coverage })}`);
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(8);
      setTextRGB(pdf, MUTED);
      const noteW = pdf.getTextWidth(coverageNote);
      const labelMax = Math.max(20, rowTextMax - noteW - 3);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9.5);
      setTextRGB(pdf, enabled ? INK : DIM);
      const sanitizedLabel = S(strategyLabel);
      let drawnLabel = sanitizedLabel;
      if (pdf.getTextWidth(drawnLabel) > labelMax) {
        // Trim label with ellipsis so the note still fits on one line.
        const ellipsis = "...";
        let lo = 0;
        let hi = drawnLabel.length;
        while (lo < hi) {
          const mid = Math.ceil((lo + hi) / 2);
          if (pdf.getTextWidth(drawnLabel.slice(0, mid) + ellipsis) <= labelMax) lo = mid;
          else hi = mid - 1;
        }
        drawnLabel = drawnLabel.slice(0, lo).replace(/\s+$/, "") + ellipsis;
      }
      pdf.text(drawnLabel, rowTextLeft, rowY + stratRowH / 2 + 1);
      const labelW = pdf.getTextWidth(drawnLabel);
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(8);
      setTextRGB(pdf, MUTED);
      pdf.text(coverageNote, rowTextLeft + labelW + 3, rowY + stratRowH / 2 + 1);
    } else {
      drawFitted(pdf, S(strategyLabel), rowTextLeft, rowY + stratRowH / 2 + 1, rowTextMax);
    }

    if (idx > 0) {
      setDrawRGB(pdf, LIGHT_BORDER);
      pdf.setLineWidth(0.2);
      pdf.line(MARGIN + 4, rowY, PAGE_W - MARGIN - 4, rowY);
    }
  });
  y += tableH + 5;

  drawSectionHeading(pdf, S, y, t("pdf.exec_summary"), t("pdf.eyebrow_sales"));
  y += 5;
  const FOOTER_TOP = PAGE_H - 17;
  const summaryH = Math.max(14, FOOTER_TOP - y);
  setFillRGB(pdf, SURFACE);
  setDrawRGB(pdf, LIGHT_BORDER);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(MARGIN, y, COL_W, summaryH, 2, 2, "FD");

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  if (executiveSummary && executiveSummary.trim().length > 0) {
    setTextRGB(pdf, INK);
    const lineH = 4.2;
    const maxLines = Math.max(1, Math.floor((summaryH - 8) / lineH));
    drawWrapped(pdf, S(executiveSummary), MARGIN + 5, y + 6, COL_W - 10, lineH, maxLines);
  } else {
    pdf.setFont("helvetica", "italic");
    setTextRGB(pdf, DIM);
    pdf.text(S(t("executive.no_summary")), MARGIN + 5, y + 6);
  }

  drawPageFooter(pdf, S, { pageNum: 2, pageTotal: 2 });

  const safeName = (state.clientName || "Helvar")
    .replace(/[^a-zA-Z0-9-_ ]/g, "")
    .trim()
    .replace(/\s+/g, "_");
  pdf.save(`${safeName}_Helvar_Business_Case.pdf`);
}
